/**
 * ============================================================================
 * TEMPLATES STORE - CACHE RETRIEVAL BY FOLDER
 * ============================================================================
 * 
 * Functions for retrieving cached templates filtered by folder hierarchy.
 * 
 * Features:
 * - Filters by folder ID
 * - Includes items in subfolders recursively
 * - Supports root-level filtering (no folder)
 * 
 * @module templates/cache/byFolder
 */

import { getAllSubfolderIds } from "../../folderStore";
import { templatesStore } from "../store";
import type { GlossaryBlueprint } from "@/types-games";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an item is in the root (no folder assigned).
 */
function isInRoot(folder: string | null | undefined): boolean {
  return !folder || folder === null || folder === '';
}

/**
 * Check if an item's folder is in a given set of folder IDs.
 */
function isInFolderSet(itemFolder: string | null | undefined, folderIds: string[]): boolean {
  return !!itemFolder && folderIds.includes(itemFolder);
}

// ============================================================================
// CACHE RETRIEVAL BY FOLDER
// ============================================================================

/**
 * Get cached templates for a specific game and folder (including subfolders).
 * 
 * Behavior:
 * - If folderId is null/undefined/empty: Returns templates in root (no folder)
 * - If folderId is provided: Returns templates in that folder AND all subfolders
 * 
 * @param gameId - The game ID to filter by
 * @param folderId - The folder ID to filter by (null/undefined for root items)
 * @returns Array of cached templates in the specified folder hierarchy
 * 
 * @example
 * ```ts
 * // Get root-level templates
 * const rootTemplates = getCachedTemplatesByFolder(gameId);
 * 
 * // Get templates in a specific folder (including subfolders)
 * const folderTemplates = getCachedTemplatesByFolder(gameId, folderId);
 * ```
 */
export function getCachedTemplatesByFolder(gameId: string, folderId?: string | null): GlossaryBlueprint[] {
  const allTemplates = templatesStore.getData();
  
  return allTemplates.filter(template => {
    // Filter by game
    if (template.game_id !== gameId) return false;
    
    // Handle folder filtering
    if (isInRoot(folderId)) {
      // Show templates in root (no folder)
      return isInRoot(template.folder);
    } else {
      // Show templates in specific folder and all its subfolders
      const allFolderIds = getAllSubfolderIds(gameId, 'blueprints', folderId!);
      return isInFolderSet(template.folder, allFolderIds);
    }
  });
}
